// src/components/tasks/TaskCard.tsx
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { useAuthStore } from "@/store/authStore"
import { toast } from "@/hooks/use-toast"
import { MessageCircle, Trash2, CheckCircle2, CalendarIcon, ArrowRightLeft } from "lucide-react"
import type { ExtendedTask } from "@/types/task"
import { db } from "@/lib/firebase"
import { doc, updateDoc, deleteDoc, arrayUnion, serverTimestamp } from "firebase/firestore"
import { useState } from "react"
import { USERS_WITHOUT_APPROVAL_BUTTONS } from "@/constants/users"

// === ДИЗАЙН-СЛОЙ ===
import { taskCardTheme } from "./ui/TaskCardUI"

const employees = [
  { id: 1, name: "Андреев Алексей Евгеньевич" },
  { id: 2, name: "Багаутдинов Ахмет Айратович" },
  { id: 3, name: "Барладян Ирина Алексеевна" },
  { id: 4, name: "Павлик Кристина Валентиновна" },
  { id: 5, name: "Султанова Диана Рашитовна" },
  { id: 6, name: "Казарин Сергей Константинович" },
  { id: 7, name: "Степанушкин Сергей Вадимович" },
]

const CAN_ASSIGN_TO = {
  1: [1, 2, 3, 4, 5, 6, 7],
  2: [2, 3, 4, 5, 6, 7],
  3: [3, 5, 6, 7],
  4: [4, 6, 7],
  5: [],
  6: [],
  7: [],
}

interface TaskCardProps {
  task: ExtendedTask
  onSelect: (task: ExtendedTask) => void
}

export function TaskCard({ task, onSelect }: TaskCardProps) {
  const { user } = useAuthStore()
  const userId = Number(user?.id ?? 0)
  const isExecutor = userId === Number(task.assigneeId)
  const isAdmin = userId === 4
  const isUrgent = task.urgency === "urgent"
  const isNewTask = task.unreadBy?.includes(userId)
  const unreadMessages = task.unreadMessagesBy?.[userId] || 0

  const isCreator = userId === Number(task.createdById) || userId === Number(task.creatorId)
  const isExcluded = USERS_WITHOUT_APPROVAL_BUTTONS.includes(userId)
  const canShowApprovalButtons = isCreator && !isExcluded && task.approvalStatus === "in_review"

  const [showReassign, setShowReassign] = useState(false)
  const [newAssigneeId, setNewAssigneeId] = useState<number | null>(null)

  const [showAddObserver, setShowAddObserver] = useState(false)
  const [newObserverId, setNewObserverId] = useState<number | null>(null)

  let progress = 0
  if (task.approvalStatus === "accepted" || task.approvalStatus === "rework") progress = 10
  else if (task.approvalStatus === "in_review" && task.approvalCount === 1) progress = 35
  else if (task.approvalStatus === "in_review" && task.approvalCount >= 2) progress = 70
  else if (task.approvalStatus === "approved") progress = 100

  const isOverdue = () => {
    if (!task.deadline) return false
    const deadline = new Date(task.deadline)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return deadline < today
  }

  const updateApprovalStatus = async (newStatus: ExtendedTask["approvalStatus"]) => {
    try {
      let newCount = task.approvalCount || 0
      if (newStatus === "in_review") newCount += 1

      await updateDoc(doc(db, "tasks", task.id!), {
        approvalStatus: newStatus,
        approvalCount: newCount,
      })

      toast({ title: "Статус обновлён" })
    } catch (error) {
      console.error("Ошибка обновления статуса:", error)
      toast({ title: "Ошибка", variant: "destructive" })
    }
  }

  const deleteTask = async () => {
    try {
      await deleteDoc(doc(db, "tasks", task.id!))
      toast({ title: "Задача удалена", variant: "destructive" })
    } catch (error) {
      console.error("Ошибка удаления:", error)
      toast({ title: "Ошибка", variant: "destructive" })
    }
  }

  const handleReassign = async () => {
    if (!newAssigneeId) return
    const newName = employees.find(e => e.id === newAssigneeId)?.name || ""

    try {
      await updateDoc(doc(db, "tasks", task.id!), {
        assigneeId: newAssigneeId,
        assignee: newName,
        updatedAt: serverTimestamp(),
        history: arrayUnion({
          userId,
          userName: user?.name || "Система",
          action: "reassigned",
          oldAssigneeId: task.assigneeId,
          newAssigneeId,
          date: new Date().toISOString(),
        })
      })

      toast({ title: "Задача переназначена", description: `Теперь: ${newName}` })
      setShowReassign(false)
      setNewAssigneeId(null)
    } catch (error) {
      console.error("Ошибка переназначения:", error)
      toast({ title: "Ошибка", variant: "destructive" })
    }
  }

  const handleAddObserver = async () => {
    if (!newObserverId) return
    const newName = employees.find(e => e.id === newObserverId)?.name || ""

    try {
      await updateDoc(doc(db, "tasks", task.id!), {
        observerIds: arrayUnion(newObserverId),
        observers: arrayUnion(newName),
      })

      toast({ title: "Наблюдатель добавлен", description: newName })
      setShowAddObserver(false)
      setNewObserverId(null)
    } catch (error) {
      console.error("Ошибка добавления наблюдателя:", error)
      toast({ title: "Ошибка", description: "Не удалось добавить наблюдателя", variant: "destructive" })
    }
  }

  return (
    <Card className={taskCardTheme.card}>
      <div className="p-8 cursor-pointer" onClick={() => onSelect(task)}>
        <div className="flex items-start gap-12">
          {/* Прогресс-круг — обновлённый светлый вариант */}
          <div className={taskCardTheme.progressContainer}>
            <svg width="120" height="120" viewBox="0 0 140 140">
              {/* Фоновый круг (светлее) */}
              <circle 
                cx="70" 
                cy="70" 
                r="60" 
                fill="none" 
                stroke={taskCardTheme.progressBackground} 
                strokeWidth="12" 
              />
              
              {/* Прогресс (светлый фиолетовый) */}
              <circle
                cx="70" 
                cy="70"
                r="60"
                fill="none"
                stroke={taskCardTheme.progressColor(progress)}
                strokeWidth="12"
                strokeDasharray="377"
                strokeDashoffset={377 * (1 - progress / 100)}
                transform="rotate(-90 70 70)"
                className={taskCardTheme.progressSvg}
                strokeLinecap="round"
              />
              
              {/* Процент */}
              <text 
                x="70" 
                y="80" 
                textAnchor="middle" 
                className="text-4xl font-bold fill-white"
              >
                {progress}%
              </text>
            </svg>
          </div>

          <div className="flex-1">
            <div className={taskCardTheme.titleContainer}>
              <h3 className={taskCardTheme.title}>{task.title}</h3>
              {isUrgent && <Badge className="bg-red-600 text-white px-4 py-1 text-sm font-bold">СРОЧНО</Badge>}
            </div>

            <p className={taskCardTheme.infoText}>
              Исполнитель: <span className="font-medium">{task.assignee}</span>
            </p>
            {task.coAssignees && task.coAssignees.length > 0 && (
              <p className={taskCardTheme.infoText}>
                Соисполнители: <span className="font-medium">{task.coAssignees.join(", ")}</span>
              </p>
            )}
            {task.observers && task.observers.length > 0 && (
              <p className={taskCardTheme.observersText}>
                Наблюдатели: <span className="font-medium">{task.observers.join(", ")}</span>
              </p>
            )}

            <p className={taskCardTheme.deadline(isOverdue())}>
              <CalendarIcon className="h-5 w-5" />
              Срок: {new Date(task.deadline).toLocaleDateString("ru-RU")}
              {isOverdue() && <span className="text-xs ml-2">(просрочено)</span>}
            </p>

            <div className="flex flex-wrap gap-3">
              {isExecutor && (task.approvalStatus === "new" || task.approvalStatus === "rework") && (
                <Button size="sm" onClick={(e) => { e.stopPropagation(); updateApprovalStatus("accepted") }} className={taskCardTheme.buttonAccepted}>
                  Принято
                </Button>
              )}
              {isExecutor && task.approvalStatus === "accepted" && (
                <Button size="sm" onClick={(e) => { e.stopPropagation(); updateApprovalStatus("in_review") }} className={taskCardTheme.buttonInReview}>
                  Отправить на согласование
                </Button>
              )}

              {canShowApprovalButtons && (
                <>
                  <Button variant="destructive" size="sm" onClick={(e) => { e.stopPropagation(); updateApprovalStatus("rework") }} className={taskCardTheme.buttonRework}>
                    На доработку
                  </Button>
                  <Button size="sm" onClick={(e) => { e.stopPropagation(); updateApprovalStatus("approved") }} className={taskCardTheme.buttonApproved}>
                    Согласовано
                  </Button>
                </>
              )}

              {task.approvalStatus === "approved" && (
                <div className={taskCardTheme.approvedBadge}>
                  <CheckCircle2 className="h-4 w-4" />
                  Готово
                </div>
              )}

              <Button size="sm" onClick={(e) => { e.stopPropagation(); onSelect(task) }} className={taskCardTheme.buttonChat}>
                <MessageCircle className="h-4 w-4 mr-2" /> Чат
              </Button>

              {![5, 6, 7].includes(userId) && CAN_ASSIGN_TO[userId]?.length > 0 && (
                <Button size="sm" onClick={(e) => { e.stopPropagation(); setShowReassign(true); setNewAssigneeId(null) }} className={taskCardTheme.buttonReassign}>
                  <ArrowRightLeft className="h-4 w-4 mr-2" /> Перенаправить
                </Button>
              )}

              <Button size="sm" onClick={(e) => { e.stopPropagation(); setShowAddObserver(true); setNewObserverId(null) }} className={taskCardTheme.buttonAddObserver}>
                Добавить наблюдателя
              </Button>

              {isAdmin && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm" onClick={(e) => e.stopPropagation()} className={taskCardTheme.buttonDelete}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Удалить
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className={taskCardTheme.dialogContent}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Удалить задачу?</AlertDialogTitle>
                      <AlertDialogDescription className="text-gray-300">
                        Это действие нельзя отменить. Задача будет удалена навсегда.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Отмена</AlertDialogCancel>
                      <AlertDialogAction onClick={deleteTask}>Удалить</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </div>
      </div>

      {isNewTask && <Badge className={taskCardTheme.badgeNew}>Новая задача</Badge>}
      {unreadMessages > 0 && (
        <Badge className={taskCardTheme.badgeUnread}>
          {unreadMessages > 99 ? "99+" : unreadMessages}
        </Badge>
      )}

      {/* Диалоги переназначения и наблюдателей */}
      <Dialog open={showReassign} onOpenChange={setShowReassign}>
        <DialogContent className={taskCardTheme.dialogContent}>
          <DialogHeader>
            <DialogTitle>Переназначить задачу</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Label className="text-purple-200">Новый исполнитель</Label>
            <Select onValueChange={(v) => setNewAssigneeId(+v)} value={newAssigneeId?.toString() ?? ""}>
              <SelectTrigger className={taskCardTheme.selectTrigger}>
                <SelectValue placeholder="Выберите нового исполнителя" />
              </SelectTrigger>
              <SelectContent className={taskCardTheme.selectContent}>
                {employees.filter(e => CAN_ASSIGN_TO[userId]?.includes(e.id) && e.id !== task.assigneeId).map(e => (
                  <SelectItem key={e.id} value={e.id.toString()} className="text-white hover:bg-purple-800/70 focus:bg-purple-800/70 cursor-pointer transition-colors">
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-3">
            <Button variant="ghost" onClick={() => setShowReassign(false)} className="text-gray-300 hover:text-white hover:bg-slate-800">Отмена</Button>
            <Button disabled={!newAssigneeId} onClick={handleReassign} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">Переназначить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddObserver} onOpenChange={setShowAddObserver}>
        <DialogContent className={taskCardTheme.dialogContent}>
          <DialogHeader>
            <DialogTitle>Добавить наблюдателя</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Label className="text-purple-200">Выберите сотрудника</Label>
            <Select onValueChange={(v) => setNewObserverId(+v)} value={newObserverId?.toString() ?? ""}>
              <SelectTrigger className={taskCardTheme.selectTrigger}>
                <SelectValue placeholder="Выберите наблюдателя" />
              </SelectTrigger>
              <SelectContent className={taskCardTheme.selectContent}>
                {employees.filter(e => e.id !== userId && !task.observerIds?.includes(e.id)).map(e => (
                  <SelectItem key={e.id} value={e.id.toString()} className="text-white hover:bg-purple-800/70 focus:bg-purple-800/70 cursor-pointer transition-colors">
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-3">
            <Button variant="ghost" onClick={() => setShowAddObserver(false)} className="text-gray-300 hover:text-white hover:bg-slate-800">Отмена</Button>
            <Button disabled={!newObserverId} onClick={handleAddObserver} className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700">Добавить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}